"use client"

import { useEffect, useMemo, useState } from "react"
import Link from "next/link"
import { Check, ArrowRight, ArrowUpRight } from "lucide-react"
import { useTranslations } from "next-intl"
import { PublicNavGlass, type GlassNavMode } from "@/components/public-nav-glass"
import { PublicFooter } from "@/components/public-footer"
import { BoothWordmark } from "@/components/booth-wordmark"
import { PricingFaq } from "@/components/pricing/pricing-faq"
import { BOOTH_RENT_PLATFORM_FEE_LABEL } from "@/lib/platform-fee"
import { BOOKINGS_FEE_LABEL, BOOKINGS_FEE_MINIMUM_LABEL, MINIMUM_ONLINE_BOOKING_LABEL } from "@/lib/booking-fees"
import {
  Eyebrow,
  GlassCard,
  GlassSegmentControl,
  GlassSurface,
  Reveal,
  displayHeadingStyle,
} from "@/components/glass/glass-kit"

type OwnerFeature = { label: string; badge: "unlimited" | "always" | null }
type Faq = { q: string; a: string }

type Audience = "owner" | "barber"

const AUDIENCES: Audience[] = ["owner", "barber"]

/** Owner pricing is the default. ?audience= reads after mount so SSR/hydration stay in sync.
 *  Clients don't pay anything on BOOTH, so a stale ?audience=client link (still used by a
 *  couple of client-facing nav entries) just falls back to owner rather than a dead tab. */
function audienceFromSearch(search: string): Audience {
  const audience = new URLSearchParams(search).get("audience")
  if (audience === "owner" || audience === "barber") return audience
  return "owner"
}

/* ── Glass primitives local to pricing ─────────────────────────── */

// Small-caps label inside a plan card. The page-level sections use the
// kit's <Eyebrow>; this is the denser variant for card interiors.
function Kicker({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-medium uppercase tracking-[0.16em]" style={{ color: "var(--pg-faint)" }}>
      {children}
    </p>
  )
}

function CheckDot() {
  return (
    <span
      className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full"
      style={{ background: "var(--pg-card)", border: "1px solid var(--pg-card-edge)", boxShadow: "inset 0 1px 0 var(--pg-gloss)" }}
    >
      <Check className="h-3 w-3" strokeWidth={2.5} style={{ color: "var(--pg-fg)" }} />
    </span>
  )
}

function FeatureCheck({ label }: { label: string }) {
  return (
    <li className="flex items-start gap-3">
      <CheckDot />
      <span className="text-[14px] leading-snug" style={{ color: "var(--pg-fg)" }}>
        {label}
      </span>
    </li>
  )
}

// Translucent tint chips, same recipe as the landing's "Paid" pill — a solid
// pastel chip is the one thing that never looks like it belongs on glass.
const BADGE: Record<NonNullable<OwnerFeature["badge"]>, React.CSSProperties> = {
  always: { background: "rgba(52,199,89,0.14)", color: "rgb(90,214,124)", border: "1px solid rgba(52,199,89,0.28)" },
  unlimited: { background: "rgba(255,196,0,0.14)", color: "rgb(232,184,48)", border: "1px solid rgba(255,196,0,0.28)" },
}

function OwnerFeatureRow({
  feature,
  unlimitedLabel,
  alwaysLabel,
}: {
  feature: OwnerFeature
  unlimitedLabel: string
  alwaysLabel: string
}) {
  return (
    <li className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-3">
        <CheckDot />
        <span className="text-[14px] leading-snug" style={{ color: "var(--pg-fg)" }}>
          {feature.label}
        </span>
      </div>
      {feature.badge && (
        <span
          className="shrink-0 rounded-full px-2 py-[3px] text-[9px] font-medium tracking-[0.08em]"
          style={BADGE[feature.badge]}
        >
          {feature.badge === "always" ? alwaysLabel : unlimitedLabel}
        </span>
      )}
    </li>
  )
}

// Sized off the house Button (h-10 / text-sm / font-medium) — same pair the
// landing's closing CTA uses, so a visitor crossing from / to /pricing sees
// one button language.
function PrimaryCta({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      className="group inline-flex h-10 items-center justify-center gap-1.5 rounded-full px-5 text-sm font-medium transition-transform duration-200 hover:scale-[1.02] active:scale-95"
      style={{ background: "var(--pg-invert-bg)", color: "var(--pg-invert-fg)" }}
    >
      {children}
      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
    </Link>
  )
}

function GlassCta({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      className="group inline-flex h-10 items-center justify-center gap-1.5 rounded-full px-5 text-sm font-medium backdrop-blur-xl transition-transform duration-200 hover:scale-[1.02] active:scale-95"
      style={{ background: "var(--pg-card)", border: "1px solid var(--pg-card-edge)", color: "var(--pg-fg)" }}
    >
      {children}
      <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </Link>
  )
}

function Price({ value, suffix }: { value: string; suffix?: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[clamp(2.75rem,6vw,3.5rem)] font-semibold leading-none tracking-[-0.04em]">{value}</span>
      {suffix ? (
        <span className="text-[15px]" style={{ color: "var(--pg-muted)" }}>
          {suffix}
        </span>
      ) : null}
    </div>
  )
}

export default function PricingPage() {
  const [mode, setMode] = useState<GlassNavMode>("dark")
  const [activeTab, setActiveTab] = useState<Audience>("owner")
  const t = useTranslations("pages.pricing")

  useEffect(() => {
    setActiveTab(audienceFromSearch(window.location.search))
  }, [])

  const fee = BOOTH_RENT_PLATFORM_FEE_LABEL
  const bookingsFee = BOOKINGS_FEE_LABEL
  const bookingsFeeMin = BOOKINGS_FEE_MINIMUM_LABEL
  const bookingsMinPrice = MINIMUM_ONLINE_BOOKING_LABEL
  const withFee = (s: string) =>
    s.split("{fee}").join(fee).split("{bookingsFee}").join(bookingsFee).split("{bookingsFeeMin}").join(bookingsFeeMin).split("{bookingsMinPrice}").join(bookingsMinPrice)
  const faqWithFee = (faqs: Faq[]): Faq[] => faqs.map((f) => ({ q: withFee(f.q), a: withFee(f.a) }))

  const barberFeatures = t.raw("barber.features") as string[]
  const ownerFeatures = t.raw("owner.features") as OwnerFeature[]
  const ownerFaqs = faqWithFee(t.raw("faq.owner") as Faq[])
  const barberFaqs = faqWithFee(t.raw("faq.barber") as Faq[])

  const activeFaqs = activeTab === "owner" ? ownerFaqs : barberFaqs

  const tabOptions = useMemo(
    () =>
      AUDIENCES.map((tab) => ({
        value: tab,
        label: t(`tabs.${tab}`),
      })),
    [t],
  )

  return (
    <GlassSurface mode={mode}>
      <PublicNavGlass mode={mode} onModeChange={setMode} />

      <main className="mx-auto w-full max-w-6xl px-4 pb-24 pt-28 sm:px-6 sm:pt-32 lg:px-8">
        <header className="mx-auto max-w-3xl text-center">
          <Reveal>
            <Eyebrow>{t("hero.eyebrow")}</Eyebrow>
            <h1 className="mt-5 font-semibold" style={displayHeadingStyle()}>
              {t("hero.title")}
              <br />
              <span style={{ color: "var(--pg-muted)" }}>{t("hero.subtitle")}</span>
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-[15px] leading-relaxed sm:text-base" style={{ color: "var(--pg-muted)" }}>
              {t("hero.tagline")}
              <br />
              {t("hero.taglineLine2")}
            </p>

            {/* Sits on the surface's own tokens now — it used to be force-painted
                with the light palette, which is why it read as a gray pill
                floating on a black page. */}
            <div className="mt-9 flex justify-center">
              <GlassSegmentControl
                value={activeTab}
                onChange={(tab) => setActiveTab(tab)}
                options={tabOptions}
                ariaLabel={t("tabs.label")}
              />
            </div>
          </Reveal>
        </header>

        {/* key is "plan-<tab>", not just "<tab>" — this div and <PricingFaq>
            below are siblings under the same parent, and both used to key
            off the bare tab value ("owner"/"barber"). Two siblings sharing
            one key is a real React bug (not just a console warning): it
            broke reconciliation on tab switch, so the old plan card never
            unmounted and both tabs' content rendered stacked at once. */}
        <div key={`plan-${activeTab}`} className="mt-12 sm:mt-14">
          {activeTab === "owner" && (
            <Reveal>
              <div className="mx-auto max-w-2xl">
                <GlassCard className="p-7 sm:p-10">
                  <Kicker>{t("owner.kicker")}</Kicker>
                  <div className="mt-4 flex items-center" aria-label={t("owner.logoAria")}>
                    {/* Follows the glass surface's own mode, not the app theme —
                        the app defaults to light, which rendered the black-text
                        logo invisibly on the dark card above "Free". */}
                    <BoothWordmark surface={mode} className="h-3.5 w-auto sm:h-4" />
                  </div>
                  <div className="mt-6">
                    <Price value={t("owner.price")} suffix={t("owner.priceSuffix") || undefined} />
                  </div>
                  <p className="mt-4 text-[14.5px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
                    {t("owner.subline")}
                  </p>
                  <div className="mt-7">
                    <PrimaryCta href="/auth/signup">{t("owner.cta")}</PrimaryCta>
                  </div>

                  <div className="mt-10 pt-8" style={{ borderTop: "1px solid var(--pg-line)" }}>
                    <Kicker>{t("owner.includedLabel")}</Kicker>
                    <ul className="mt-5 grid gap-4 sm:grid-cols-2 sm:gap-x-8">
                      {ownerFeatures.map((f) => (
                        <OwnerFeatureRow
                          key={f.label}
                          feature={f}
                          unlimitedLabel={t("owner.badges.unlimited")}
                          alwaysLabel={t("owner.badges.always")}
                        />
                      ))}
                    </ul>
                  </div>
                </GlassCard>
              </div>
            </Reveal>
          )}

          {activeTab === "barber" && (
            <div className="mx-auto max-w-3xl space-y-4">
              <Reveal>
                <GlassCard className="p-7 sm:p-10">
                  <div className="grid gap-10 md:grid-cols-2 md:gap-12">
                    <div>
                      <Kicker>{t("barber.kicker")}</Kicker>
                      <h3 className="mt-3 text-2xl font-semibold tracking-[-0.02em]">{t("barber.title")}</h3>
                      <p className="mt-3 text-[14.5px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
                        {t("barber.desc", { fee })}
                      </p>
                      <ul className="mt-6 space-y-3.5">
                        {barberFeatures.map((feature) => (
                          <FeatureCheck key={feature} label={feature} />
                        ))}
                      </ul>
                    </div>

                    <div className="flex flex-col justify-center gap-5">
                      {/* Nested tile: slightly brighter than the card so it
                          reads as sitting ON the glass, not cut into it. */}
                      <div
                        className="rounded-[18px] p-5"
                        style={{
                          background: "var(--pg-card)",
                          border: "1px solid var(--pg-card-edge)",
                          boxShadow: "inset 0 1px 0 var(--pg-gloss)",
                        }}
                      >
                        <Kicker>{t("barber.details.label")}</Kicker>
                        <p className="mt-3 text-[14px] leading-relaxed" style={{ color: "var(--pg-fg)" }}>
                          {t.rich("barber.details.body", {
                            fee,
                            strong: (chunks) => <strong className="font-semibold">{chunks}</strong>,
                          })}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2.5">
                        <PrimaryCta href="/auth/signup">{t("barber.cta")}</PrimaryCta>
                        <GlassCta href="/listings">{t("barber.browse")}</GlassCta>
                      </div>
                    </div>
                  </div>
                </GlassCard>
              </Reveal>

              <Reveal delay={80}>
                <GlassCard className="p-7 sm:p-10">
                  <div className="grid items-center gap-10 md:grid-cols-2 md:gap-12">
                    <div>
                      <Kicker>{t("barber.bookings.kicker")}</Kicker>
                      <h3 className="mt-3 text-2xl font-semibold tracking-[-0.02em]">{t("barber.bookings.title")}</h3>
                      <div className="mt-5">
                        <Price value={t("barber.bookings.price")} suffix={t("barber.bookings.priceSuffix")} />
                      </div>
                      <p className="mt-4 text-[14.5px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
                        {withFee(t.raw("barber.bookings.subline") as string)}
                      </p>
                      <div className="mt-7">
                        <PrimaryCta href="/dashboard/barber/bookings">{t("barber.bookings.cta")}</PrimaryCta>
                      </div>
                    </div>
                    <div>
                      <ul className="space-y-3.5">
                        {(t.raw("barber.bookings.features") as string[]).map(withFee).map((feature) => (
                          <FeatureCheck key={feature} label={feature} />
                        ))}
                      </ul>
                      <p className="mt-5 text-[12.5px] leading-relaxed" style={{ color: "var(--pg-faint)" }}>
                        {withFee(t.raw("barber.bookings.note") as string)}
                      </p>
                    </div>
                  </div>
                </GlassCard>
              </Reveal>
            </div>
          )}
        </div>

        <PricingFaq
          key={`faq-${activeTab}`}
          faqs={activeFaqs}
          eyebrow={t("faq.eyebrow")}
          title={t("faq.title")}
          subtitle={t(`faq.subtitle.${activeTab}`)}
        />
      </main>

      <PublicFooter />
    </GlassSurface>
  )
}
